/**
 * «Юниверситет» (28.09): задание «по порядку». Шаги показываем перемешанными (если
 * перемешивание случайно совпало с верным порядком — разворачиваем), кнопки ↑/↓ меняют
 * шаг с соседом. Зачёт — всё или ничего; при ошибке — верный порядок в отзыве.
 * Фокус едет вместе с перемещённым шагом: с клавиатуры можно гонять шаг стрелками подряд.
 */
import { useEffect, useRef, useState } from "react";
import { shuffle } from "./ctx";
import { Rich } from "./rich";
import type { OnAnswered } from "./ItemView";
import css from "./run.module.css";
import ui from "./uni.module.css";

export function ItemSteps({ steps, onDone }: { steps: string[]; onDone: OnAnswered }) {
  const [cur, setCur] = useState<number[]>(() => {
    const c = shuffle(steps.map((_, k) => k));
    return c.length > 1 && c.every((v, k) => v === k) ? c.reverse() : c;
  });
  const [checked, setChecked] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  const btns = useRef(new Map<string, HTMLButtonElement>());

  useEffect(() => {
    if (focus) btns.current.get(focus)?.focus();
  }, [focus, cur]);

  const move = (p: number, d: -1 | 1) => {
    const q = p + d;
    if (q < 0 || q >= cur.length) return;
    const next = [...cur];
    [next[p], next[q]] = [next[q]!, next[p]!];
    setCur(next);
    const k = cur[p]!;
    /* у края та же кнопка станет неактивной — фокус на соседнюю */
    const atEdge = (d === -1 && q === 0) || (d === 1 && q === next.length - 1);
    setFocus(`${k}:${(d === -1) !== atEdge ? "u" : "d"}`);
  };

  const check = () => {
    if (checked) return;
    setChecked(true);
    const ok = cur.every((k, p) => k === p);
    onDone(ok, ok ? "" : `Правильно: ${steps.map((s, k) => `${k + 1}) ${s}`).join("; ")}. `);
  };

  const refFor = (id: string) => (el: HTMLButtonElement | null) => {
    if (el) btns.current.set(id, el); else btns.current.delete(id);
  };

  return (
    <>
      <ol className={css.steps}>
        {cur.map((k, p) => (
          <li key={k} className={checked ? (k === p ? css.right : css.wrong) : ""} data-fb-part="шаг">
            <span><Rich text={steps[k]} /></span>
            {!checked ? (
              <span className={css.mv}>
                <button
                  ref={refFor(`${k}:u`)}
                  type="button"
                  aria-label={`Поднять шаг ${p + 1}`}
                  disabled={p === 0}
                  onClick={() => move(p, -1)}
                >↑</button>
                <button
                  ref={refFor(`${k}:d`)}
                  type="button"
                  aria-label={`Опустить шаг ${p + 1}`}
                  disabled={p === cur.length - 1}
                  onClick={() => move(p, 1)}
                >↓</button>
              </span>
            ) : null}
          </li>
        ))}
      </ol>
      {!checked ? (
        <div className={ui.acts}>
          <button type="button" className={ui.btn} onClick={check}>Проверить</button>
        </div>
      ) : null}
    </>
  );
}
